/**
 * Central Marketing & Billing Display Configuration
 *
 * Single source of truth for plan names, display prices, trial rules, and
 * verified feature lists across the public marketing site (/ , /pricing, /register)
 * and the in-app billing UI fallbacks (TrialExpiredOverlay, Settings).
 *
 * CEO-APPROVED COMMERCIAL LAUNCH PRICING:
 * - Starter: $99 USD/month per facility (`priceApproved: true`)
 * - Operations: $199 USD/month per facility (`priceApproved: true`)
 * - 30-day full-access trial (full Operations feature access) · No credit card required at signup · No automatic charge at trial expiration
 */

export interface PlanDisplayConfig {
    id: 'starter' | 'operations';
    name: string;
    priceMonthlyUsd: number;
    pendingPriceRangeUsd?: string;
    currency: 'USD';
    interval: 'month';
    billingUnit: 'per facility';
    recommended?: boolean;
    priceApproved: boolean;
    founderReviewBadge?: string;
    founderReviewDetail?: string;
    tagline: string;
    description: string;
    features: string[];
}

export const PRICING_CONFIG = {
    trialDays: 30,
    creditCardRequiredAtSignup: false,
    currency: 'USD' as const,
    interval: 'month' as const,
    billingUnit: 'per facility' as const,
    startingPriceIntro: 'Plans start at $99 USD/month.',
    supportEmail: 'support@baywhiz.com',

    trialSummary: {
        headline: '30-day full-access trial · No credit card required',
        subheadline:
            'Every new warehouse facility gets 30 days of full Operations feature access to BayWhiz. You will never be billed automatically at the end of your trial.',
        expirationExplanation:
            'Because no credit card is required at signup, there is no automatic charge at trial expiration. If no paid subscription is activated by day 30, operational access pauses safely while your facility setup, docks, POs, and appointment history remain intact. Contact support@baywhiz.com to activate Starter or Operations whenever you are ready.',
        carrierAccessNote:
            'Carriers never pay to request appointments or track bookings through your warehouse booking link or the Carrier Portal.',
    },

    plans: {
        starter: {
            id: 'starter',
            name: 'Starter',
            priceMonthlyUsd: 99,
            currency: 'USD',
            interval: 'month',
            billingUnit: 'per facility',
            recommended: false,
            priceApproved: true,
            tagline:
                'Transparent monthly subscription for a single warehouse facility replacing manual spreadsheet and email dock scheduling.',
            description:
                'Per-facility monthly subscription at $99 USD/month after your 30-day full-access trial.',
            features: [
                'Unlimited dock doors and warehouse users',
                'Live warehouse schedule and dock board',
                'Carrier booking requests and PO validation',
                'Manual appointment approval and management',
                'Dock assignment, reassignment, check-in and checkout',
                'Purchase Order management',
                'CSV PO import',
                'Basic receiving and discrepancy recording',
                'Appointment history and schedule CSV export',
                'Carrier contact directory',
            ],
        } satisfies PlanDisplayConfig,

        operations: {
            id: 'operations',
            name: 'Operations',
            priceMonthlyUsd: 199,
            currency: 'USD',
            interval: 'month',
            billingUnit: 'per facility',
            recommended: true,
            priceApproved: true,
            tagline:
                'Complete warehouse dock operations with rule-based automatic approval, consolidated loads, advanced dwell & carrier analytics, and deterministic operational assistance.',
            description:
                'Full warehouse dock scheduling, consolidated loads, advanced analytics, and rule-based operational assistance at $199 USD/month per facility.',
            features: [
                'Everything in Starter',
                'Rule-based automatic appointment approval',
                'Consolidated Loads and linked outbound Sales Orders',
                'Excel .xlsx PO import',
                'Advanced dwell and detention analytics',
                'Carrier reliability scorecards',
                'Advanced receiving exception analysis and shortage follow-up POs',
                'Historical delay-risk analysis and rule-based operational assistance',
                'Deterministic Copilot for operational schedule, dwell, and risk queries',
            ],
        } satisfies PlanDisplayConfig,
    },
} as const;

export const MARKETING_PLAN_LIST: PlanDisplayConfig[] = [
    PRICING_CONFIG.plans.starter,
    PRICING_CONFIG.plans.operations,
];
